import { useState, type ReactNode } from "react";
import type { CapabilityState } from "../shared/types";
import { Icon } from "./Icon";
import type { CloudflareTimelineStatus } from "./types";

export function initials(user: { name: string }): string {
  return user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function PanelState({
  error = false,
  label,
}: {
  error?: boolean;
  label: string;
}) {
  return (
    <section
      className="panel-card panel-load-state"
      role={error ? "alert" : "status"}
    >
      {!error && <span className="spinner spinner--dark" />}
      <strong>
        {error ? "Unable to load this view" : `Loading ${label}…`}
      </strong>
      {error && <span>Try this view again or reload the dashboard.</span>}
    </section>
  );
}

/**
 * A background refetch failed while cached data is still on screen. The panel
 * keeps rendering that data instead of collapsing into `PanelState error`,
 * which would destroy any open one-time-secret dialog.
 */
export function RefreshFailedNotice() {
  return (
    <div className="notice notice--warning page-notice" role="status">
      Could not refresh — showing the last loaded data.
    </div>
  );
}

/** The Cloudflare API connection state, shown in panel headers. */
export function CloudflareStatusBadge({
  state,
}: {
  state: CloudflareTimelineStatus["state"];
}) {
  return (
    <div className={`integration-status integration-status--${state}`}>
      <span aria-hidden="true" />
      Cloudflare {state.replaceAll("_", " ")}
    </div>
  );
}

/**
 * Flags a section whose Cloudflare capability is unavailable, so a degraded
 * read does not look like an empty collection.
 */
export function CapabilityBadge({
  capability,
}: {
  capability: CapabilityState;
}) {
  if (capability === "available") return null;
  return (
    <span className="panel-status panel-status--warning">
      {capability.replaceAll("_", " ")}
    </span>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <header className="admin-header">
      <div className="admin-header__copy">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action && <div className="admin-header__action">{action}</div>}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  detail: string;
  tone?: "neutral" | "positive" | "warning";
}) {
  return (
    <article className={`stat-card stat-card--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

/** Users and applications share the same active/disabled lifecycle. */
export type StatusFilter = "all" | "active" | "disabled";

export function PanelToolbar({
  filterLabel,
  onQueryChange,
  onStatusChange,
  query,
  searchLabel,
  searchPlaceholder,
  status,
}: {
  filterLabel: string;
  onQueryChange: (value: string) => void;
  onStatusChange: (status: StatusFilter) => void;
  query: string;
  searchLabel: string;
  searchPlaceholder: string;
  status: StatusFilter;
}) {
  return (
    <div className="directory-toolbar">
      <label className="search-field">
        <span className="sr-only">{searchLabel}</span>
        <Icon name="search" size={17} />
        <input
          aria-label={searchLabel}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder={searchPlaceholder}
          type="search"
          value={query}
        />
      </label>
      <label className="select-field select-field--compact">
        <span className="sr-only">{filterLabel}</span>
        <select
          aria-label={filterLabel}
          onChange={(event) =>
            onStatusChange(event.target.value as StatusFilter)
          }
          value={status}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </label>
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  compact = false,
  onClearFilters,
}: {
  title: string;
  hint: string;
  /** Shorter placeholder for the side-by-side Cloudflare cards. */
  compact?: boolean;
  onClearFilters?: () => void;
}) {
  return (
    <div className={`empty-state${compact ? " empty-state--compact" : ""}`}>
      <strong>{title}</strong>
      <span>{hint}</span>
      {onClearFilters && (
        <button
          className="button button--quiet"
          onClick={onClearFilters}
          type="button"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

export function CopyButton({
  value,
  label = "Copy",
}: {
  value: string;
  label?: string;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    window.setTimeout(() => setCopyState("idle"), 1800);
  }

  return (
    <button
      aria-label={`${label}: ${value}`}
      className="icon-button icon-button--surface"
      onClick={() => void copy()}
      title={
        copyState === "copied"
          ? "Copied"
          : copyState === "failed"
            ? "Copy failed"
            : label
      }
      type="button"
    >
      <Icon
        name={
          copyState === "copied"
            ? "check"
            : copyState === "failed"
              ? "warning"
              : "copy"
        }
        size={16}
      />
    </button>
  );
}
